﻿import { ErrorModal, useMesfRealtime } from "@dexteel/mesf-core";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
	Autocomplete,
	Box,
	Chip,
	CircularProgress,
	Divider,
	Grid2,
	IconButton,
	TextField,
	Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { FieldError } from "react-hook-form";
import { Link } from "react-router-dom";
import { SectionsResponse } from "../repositories/SectionRepository";
import { useSections } from "./SectionsOfPartSelector";

type SortBy = "upcoming" | "recent" | "alphabetical";

const sortSections = (sectionResponse: SectionsResponse, sortBy: SortBy) => {
	const sections = sectionResponse.sections;

	if (sortBy === "alphabetical") {
		return [...(sections || [])].sort((a, b) =>
			a.SectionName.localeCompare(b.SectionName),
		);
	}

	const events =
		sortBy === "recent"
			? sectionResponse.recentEvents
			: sectionResponse.upcomingEvents;

	return [...(sections || [])].sort((a, b) => {
		const aEvent = events?.find((e) => e.SectionId === a.SectionId);
		const bEvent = events?.find((e) => e.SectionId === b.SectionId);
		const aIndex = aEvent?.EventIndex ?? Number.MAX_VALUE;
		const bIndex = bEvent?.EventIndex ?? Number.MAX_VALUE;
		if (aIndex !== bIndex) return aIndex - bIndex;
		return a.SectionName.localeCompare(b.SectionName);
	});
};

type Props = {
	label?: string;
	value: number | null;
	readOnly?: boolean;
	onChange?: (
		value: number | null,
		name?: string | null,
		tph?: number | null,
		baseTargetTPH?: number | null,
		targetTPHPercentage?: number | null,
		millYield?: number | null,
		finishedYield?: number | null,
	) => void;
	disabled?: boolean;
	required?: boolean;
	fieldError?: FieldError;
	includeMostUsed?: boolean;
	onCurrentClick?: () => void;
	size?: "small" | "medium";
	sortBy?: SortBy;
	hideTph?: boolean;
	assetId?: number;
};

export const SectionSelector = ({
	label = "Section",
	value,
	readOnly = false,
	onChange = () => {},
	disabled = false,
	required = false,
	fieldError,
	includeMostUsed = false,
	onCurrentClick,
	size = "medium",
	sortBy = "upcoming",
	hideTph = false,
	assetId,
}: Props) => {
	const queryClient = useQueryClient();

	const [error, setError] = useState("");

	const { data, isLoading } = useSections({
		refetchInterval: 60_000,
		assetId: assetId,
		onError: (error) => setError(error.message),
	});

	const sections = data?.sections;
	const upcomingEvents = data?.upcomingEvents;

	const selectedValue =
		sections?.find((section) => section.SectionId === value) || null;

	useMesfRealtime({
		onReceiveMessage: async (author, message) => {
			if (message.replaceAll("[", "").replaceAll("]", "") == "M2.GetSections") {
				await queryClient.invalidateQueries(["sections"]);
			}
		},
	});

	const autocompleteComponent = (
		<Autocomplete
			id="clear-on-escape"
			size="small"
			clearOnEscape
			autoHighlight
			options={sortSections(
				data || {
					sections: [],
					upcomingEvents: [],
					recentEvents: [],
					rollChangeStats: null,
				},
				sortBy,
			)}
			getOptionLabel={(option) => option.SectionName}
			disabled={disabled}
			renderOption={(props, option) => (
				<Box
					{...props}
					component="li"
					sx={{
						display: "flex",
						flexDirection: "column",
						justifyContent: "flex-start",
						width: "100%",
					}}
				>
					<Box
						sx={{
							width: "100%",
						}}
					>
						{option.SectionName}
					</Box>
					<Box
						sx={{
							display: "flex",
							flexWrap: "wrap",
							gap: "4px",
							width: "100%",
						}}
					>
						{option.SectionFamilyName && (
							<Box sx={{ fontSize: "0.8em", color: "grey" }}>
								{`Family: ${option.SectionFamilyName}${
									!hideTph && option.TonsPerHour
										? ` | TPH: ${option.TonsPerHour}${option.TargetTPH ? ` | Base Target: ${option.TargetTPH ?? ""}` : ""}${option.TargetTPHPercentage ? ` | Target %: ${option.TargetTPHPercentage}%` : ""}`
										: ``
								}`}
							</Box>
						)}
					</Box>
				</Box>
			)}
			renderInput={(params) => (
				<TextField
					{...params}
					aria-readonly={readOnly}
					required={required}
					label={label}
					variant="outlined"
					size="small"
					fullWidth
					error={!!fieldError}
					helperText={fieldError?.message}
					sx={{
						backgroundColor: (theme) => theme.palette.background.paper,
					}}
					inputProps={{
						...params.inputProps,
						"data-test-id": "section-selector",
					}}
					InputProps={{
						...params.InputProps,
						endAdornment: (
							<>
								{isLoading ? (
									<CircularProgress color="inherit" size={20} />
								) : (
									<>
										{params.InputProps.endAdornment}
										{value && size === "medium" ? (
											<IconButton
												component={Link}
												to={`/configuration/sections/${value}`}
												target="_blank"
												size="small"
												title="See in sections"
											>
												<VisibilityIcon sx={{ fontSize: 16 }} />
											</IconButton>
										) : null}
									</>
								)}
							</>
						),
					}}
				/>
			)}
			value={selectedValue}
			onChange={(event, newValue) => {
				onChange(
					newValue?.SectionId ?? null,
					newValue?.SectionName,
					newValue?.TonsPerHour,
					newValue?.TargetTPH ?? null,
					newValue?.TargetTPHPercentage ?? null,
					newValue?.MillYield ?? null,
					newValue?.FinishedYield ?? null,
				);
			}}
		/>
	);

	if (!includeMostUsed) {
		return (
			<>
				<Box>{autocompleteComponent}</Box>
				<ErrorModal
					error={error}
					onHide={() => setError("")}
					title="Error loading sections"
				/>
			</>
		);
	}

	const nextEvents =
		upcomingEvents?.filter((e) => e.EventIndex > 1).slice(0, 10) ?? [];
	const currentEvent = upcomingEvents?.find((e) => e.EventIndex === 1);
	const currentSection = currentEvent
		? sections?.find((s) => s.SectionId === currentEvent.SectionId)
		: null;

	return (
		<>
			<Grid2 container spacing={1} alignItems="flex-end">
				<Grid2 size={{ xs: 12, md: 3 }}>{autocompleteComponent}</Grid2>
				<Grid2
					size={{ md: 8 }}
					sx={{
						display: "flex",
						alignItems: "center",
						flexWrap: "wrap",
						gap: 1.5,
						ml: 1,
					}}
				>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							alignItems: "flex-start",
							gap: 0.5,
						}}
					>
						<Typography
							variant="caption"
							color="text.disabled"
							sx={{
								fontSize: "0.65rem",
								lineHeight: 1.5,
								fontWeight: "bold",
							}}
						>
							Current
						</Typography>
						<Chip
							onClick={() => {
								if (onCurrentClick) {
									onCurrentClick();
								} else {
									onChange(
										currentSection?.SectionId ?? null,
										currentSection?.SectionName,
										currentSection?.TonsPerHour,
										currentSection?.TargetTPH,
										currentSection?.TargetTPHPercentage,
									);
								}
							}}
							label={currentSection?.SectionName ?? ""}
							size="medium"
							variant={
								currentSection?.SectionId == selectedValue?.SectionId
									? "filled"
									: "outlined"
							}
							color={
								currentSection?.SectionId == selectedValue?.SectionId
									? "primary"
									: "default"
							}
						/>
					</Box>

					{nextEvents.length > 0 && (
						<Divider
							orientation="vertical"
							flexItem
							sx={{ mx: 0.5, my: 0.5 }}
						/>
					)}

					{nextEvents.length > 0 && (
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								alignItems: "flex-start",
								gap: 0.5,
							}}
						>
							<Typography
								variant="caption"
								color="text.disabled"
								sx={{
									fontSize: "0.65rem",
									lineHeight: 1.5,
									fontWeight: "bold",
								}}
							>
								Next Sections
							</Typography>
							<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
								{nextEvents.map((event) => {
									const s = sections?.find(
										(s) => s.SectionId === event.SectionId,
									);
									const isSelected = event.SectionId === value;
									return (
										<Chip
											key={`${event.SectionId}-${event.EventIndex}`}
											label={s?.SectionName}
											size="medium"
											variant={isSelected ? "filled" : "outlined"}
											color={isSelected ? "primary" : "default"}
											onClick={() =>
												onChange(
													s?.SectionId ?? null,
													s?.SectionName,
													s?.TonsPerHour,
													s?.TargetTPH,
													s?.TargetTPHPercentage,
												)
											}
											sx={{ cursor: "pointer" }}
										/>
									);
								})}
							</Box>
						</Box>
					)}
				</Grid2>
			</Grid2>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error loading sections"
			/>
		</>
	);
};
